import {setEchoSocketId} from '@common/http/get-echo-socket-id';
import {queryClient} from '@common/http/query-client';
import {
  listNotificationsOptions,
  notificationsBaseKey,
} from '@common/notifications/notifications-queries';
import {
  playNotificationSound,
  unlockNotificationAudio,
} from '@common/notifications/play-notification-sound';
import {useQuery} from '@tanstack/react-query';
import {
  getBootstrapData,
  mergeBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {useEffect, useRef} from 'react';
import {useAuth} from '../auth/use-auth';

const POLL_MS = 15000;

export function useRealtimeNotifications() {
  const {isLoggedIn, user} = useAuth();
  const lastUnread = useRef<number | null>(null);
  const lastSeenId = useRef<string | null>(null);

  useEffect(() => {
    const unlock = () => unlockNotificationAudio();
    window.addEventListener('pointerdown', unlock, {once: true});
    window.addEventListener('keydown', unlock, {once: true});
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const query = useQuery({
    ...listNotificationsOptions({perPage: 20}),
    enabled: isLoggedIn,
    refetchInterval: () =>
      typeof document !== 'undefined' && document.visibilityState === 'visible'
        ? POLL_MS
        : false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!isLoggedIn) {
      lastUnread.current = null;
      lastSeenId.current = null;
      return;
    }

    const incoming =
      query.data?.unreadCount ??
      query.data?.data?.filter(item => !item.read_at).length ??
      user?.unread_notifications_count ??
      0;

    if (lastUnread.current === null) {
      lastUnread.current = incoming;
      lastSeenId.current = query.data?.data?.[0]?.id ?? null;
      if (user && user.unread_notifications_count !== incoming) {
        mergeBootstrapData({
          user: {...user, unread_notifications_count: incoming},
        });
      }
      return;
    }

    if (incoming > lastUnread.current) {
      const newest = query.data?.data?.[0];
      playNotificationSound();
      showDesktopNotification(newest?.data?.lines?.[0]?.content, newest?.data?.lines?.[1]?.content);
      lastSeenId.current = newest?.id ?? lastSeenId.current;
    }

    lastUnread.current = incoming;
    if (user && user.unread_notifications_count !== incoming) {
      mergeBootstrapData({
        user: {...user, unread_notifications_count: incoming},
      });
    }
  }, [isLoggedIn, query.data, user]);

  useEffect(() => {
    if (!isLoggedIn || !user?.id) {
      return;
    }
    let echo: {disconnect: () => void} | null = null;
    let cancelled = false;

    initEcho(user.id)
      .then(instance => {
        if (cancelled) {
          instance?.disconnect();
          return;
        }
        echo = instance;
      })
      .catch(() => {
        // polling still covers new notifications
      });

    return () => {
      cancelled = true;
      echo?.disconnect();
    };
  }, [isLoggedIn, user?.id]);
}

function showDesktopNotification(title?: string, body?: string) {
  if (
    typeof Notification === 'undefined' ||
    Notification.permission !== 'granted'
  ) {
    return;
  }
  try {
    new Notification(title || 'Cloud Drive', {
      body: body || 'You have a new notification',
      icon: '/images/favicon.svg',
      tag: 'cloud-drive-notification',
    });
  } catch {
    // some browsers throw if the document is not focused
  }
}

async function initEcho(userId: number) {
  const broadcasting = getBootstrapData().settings?.broadcasting;
  if (
    !broadcasting?.key ||
    broadcasting.driver === 'null' ||
    broadcasting.driver === 'log'
  ) {
    return null;
  }

  const [{default: Echo}, {default: Pusher}] = await Promise.all([
    import('laravel-echo'),
    import('pusher-js'),
  ]);

  (window as Window & {Pusher?: typeof Pusher}).Pusher = Pusher;

  const echo = new Echo({
    broadcaster:
      broadcasting.driver === 'ably' ? 'pusher' : broadcasting.driver,
    key: broadcasting.key,
    cluster: broadcasting.cluster,
    wsHost: broadcasting.host,
    wsPort: broadcasting.port,
    wssPort: broadcasting.port,
    forceTLS: broadcasting.scheme !== 'http',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: '/broadcasting/auth',
    withCredentials: true,
  } as never);

  const connector = (echo as {connector?: {pusher?: {connection?: {bind?: (event: string, cb: () => void) => void}}}}).connector;
  connector?.pusher?.connection?.bind?.('connected', () => {
    const socketId = echo.socketId?.();
    if (socketId) {
      setEchoSocketId(socketId);
    }
  });

  const onIncoming = () => {
    queryClient.invalidateQueries({queryKey: notificationsBaseKey});
  };

  echo.private(`App.User.${userId}`).notification(onIncoming);
  echo.private(`App.Models.User.${userId}`).notification(onIncoming);

  return echo as {disconnect: () => void};
}
